<template>
  <div class="main-search container page-section">
    <div class="main-search__header">
      <h1
        id="template-title"
        class="main-search__title text-mobile-paragraph-l-regular text-desktop-paragraph-l-regular col xs-span m4-8 text-center"
        v-text="$string('search.title')"
      />

      <div class="grid">
        <div class="col xs-span l4-9">
          <search-bar />

          <p
            v-show="count > 0 && query.length > 0"
            class="main-search__result-text"
            v-html="
              $string('general.predictive_search.result_text_html', {
                replace: {
                  count: count,
                  term: query,
                },
              })
            "
          />
          <p
            v-if="count === 0 && state === 'ready'"
            class="main-search__empty-text text-mobile-paragraph-s-regular text-desktop-paragraph-s-regular"
            v-text="
              $string('search.empty', {
                replace: {
                  term: query,
                },
              })
            "
          />
        </div>
      </div>
    </div>

    <product-grid
      v-show="count > 0"
      template="search"
      :default-sort-by="$variable('search.sortBy')"
      :products-per-page="$variable('settings.pagination.products')"
      show-title
      @state="state = $event"
    />

    <div v-if="pageResults.length > 0" class="main-search__pages">
      <h2
        class="main-search__subtitle text-mobile-headlines-h3 text-desktop-headlines-h3"
        v-text="
          $string('general.predictive_search.pages', {
            replace: { count: pageResults.length },
          })
        "
      />
      <div class="grid">
        <div
          v-for="page of pageResults"
          :key="page.handle"
          class="col xs-span m3 l4"
        >
          <div class="main-search__pages-card">
            <a
              v-if="hasImages(page)"
              class="main-search__pages-link"
              :href="page.url"
            >
              <span class="visually-hidden" v-text="page.title" />
              <responsive-image
                :image="page.metafield.reference.image"
                :alt="page.title"
                ratio="1:1"
                ratio-desktop="16:9"
                sizes="(min-width: 768px) 50vw, (min-width: 1024px) 33vw, 100vw"
              />
            </a>
            <div class="main-search__pages-content">
              <h2
                class="text-mobile-paragraph-l-bold text-desktop-paragraph-l-bold"
                v-text="page.title"
              />
              <a class="main-search__pages-url" :href="page.onlineStoreUrl">
                <span v-text="$string('general.predictive_search.read_more')" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="blogResults.length > 0" class="main-search__blogs">
      <h2
        class="main-search__subtitle text-mobile-headlines-h3 text-desktop-headlines-h3"
        v-text="
          $string('general.predictive_search.articles', {
            replace: { count: blogResults.length },
          })
        "
      />

      <div class="grid">
        <div
          v-for="blog of blogResults"
          :key="blog.blog_id"
          class="col xs-span m3 l4"
        >
          <article-card
            :article="blog"
            :show-excerpt="false"
            show-date
            square-ratio
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Vue: Main search (main-search)
 * -----------------------------------------------------------------------------
 * Main search page component.
 *
 */
import { mapState } from 'pinia';
import usePredictiveSearchStore from '~stores/predictive-search';

import ProductGrid from '~async/product-grid/product-grid';
import ResponsiveImage from '~global/images/responsive-image.vue';
import ArticleCard from '~async/main-blog/article-card.vue';
import SearchBar from '~async/search-bar/search-bar';

export default {
  name: 'MainSearch',

  components: {
    ArticleCard,
    ProductGrid,
    SearchBar,
    ResponsiveImage,
  },

  data() {
    return {
      count: 0,
      state: 'initial-load',
      results: [],
    };
  },

  computed: {
    /**
     * Map Pinia state.
     */
    ...mapState(usePredictiveSearchStore, ['query']),

    /**
     * Compute blog results with necessary fields.
     * - Filter by market and language metafields
     * @return {Array}
     */
    blogResults() {
      const articles = this.results.filter(
        (result) =>
          result.id.includes('Article') &&
          (result.availableMarkets === null ||
            result.availableMarkets.includes(cnvs.store.country)) &&
          (result.availableLanguages === null ||
            result.availableLanguages.includes(cnvs.store.language)),
      );
      return articles;
    },

    pageResults() {
      return this.results.filter((result) => result.id.includes('Page'));
    },
  },

  mounted() {
    cnvs.EventBus.listen('search:results', (results) => {
      this.count = results.totalCount;
      this.results = results.edges.map((edge) => edge.node);
    });
  },

  methods: {
    hasImages(page) {
      return page.metafield !== null;
    },
  },
};
</script>

<style lang="scss">
@import './main-search';
</style>
